/**
 * 169 种翻前起手牌类（hand class）的分类学。见 docs/ARCHITECTURE.md §10.3 / §10.5。
 *
 * 为什么单独一个文件：`preflop.ts` 要靠它做查表键，`ranges.ts` 要靠它把
 * 「top 20%」展开成具体组合，离线生成脚本也要靠它枚举 169 个代表手牌。
 * 如果把它放进 preflop.ts，生成脚本就得 import 一个「依赖尚未生成的 JSON」的模块，
 * 自举时必然循环；放进 ranges.ts 则会和 RANGE_ORDER（要读表）互相 import。
 *
 * 花色同构：翻前只有三种情形——对子、同花、非同花。两手牌只要属于同一类，
 * 对随机对手的 equity 完全相同（整副牌对花色是对称的），所以 169 个值就够。
 */
import type { CardId } from '../ids.js'
import { RANK_CHARS, SHORT_DECK_MIN_RANK, makeCard, rankOf, suitOf } from '../poker/cards.js'
import type { DeckVariant } from '../poker/types.js'

/** 169 个类的规范顺序：点数从 A 往下扫，上三角是同花，下三角是非同花。 */
function buildClasses(): { names: string[]; combos: number[] } {
  const names: string[] = []
  const combos: number[] = []
  for (let hi = 12; hi >= 0; hi--) {
    for (let lo = 12; lo >= 0; lo--) {
      if (hi === lo) {
        names.push(`${RANK_CHARS[hi]}${RANK_CHARS[hi]}`)
        combos.push(6) // C(4,2)
      } else if (hi > lo) {
        names.push(`${RANK_CHARS[hi]}${RANK_CHARS[lo]}s`)
        combos.push(4) // 四门花色各一
      } else {
        names.push(`${RANK_CHARS[lo]}${RANK_CHARS[hi]}o`)
        combos.push(12) // 4 × 3
      }
    }
  }
  return { names, combos }
}

const BUILT = buildClasses()

/** 169 个类名，规范顺序。索引即 classIndex，供表格与 RANGE_ORDER 复用。 */
export const HAND_CLASSES: readonly string[] = Object.freeze(BUILT.names)

/** 每个类在 52 张牌里对应多少个具体组合（对子 6 / 同花 4 / 非同花 12），总和 1326。 */
export const HAND_CLASS_COMBOS: readonly number[] = Object.freeze(BUILT.combos)

/** 全部组合数 C(52,2)。top-percent 的百分比按组合数而不是「169 个类」计。 */
export const TOTAL_COMBOS = 1326

/**
 * 按玩法的全部组合数：标准 C(52,2)=1326；短牌 C(36,2)=630。
 * 短牌只用得到 169 个类里两张点数都不低于 6 的 81 个，每个类的组合数不变（6 / 4 / 12）。
 */
export const TOTAL_COMBOS_BY_VARIANT: Readonly<Record<DeckVariant, number>> = Object.freeze({
  standard: TOTAL_COMBOS,
  shortDeck: 630,
})

/**
 * 这个类在该玩法里是否发得出来。类索引仍沿用 169 的编号空间：
 * 行 = 12 − 高牌点数、列 = 12 − 低牌点数（非同花行列互换），两者都 ≤ 12 − 6 的下标即可。
 */
export function isClassInVariant(classIndex: number, variant: DeckVariant): boolean {
  if (!Number.isInteger(classIndex) || classIndex < 0 || classIndex >= 169) return false
  if (variant !== 'shortDeck') return true
  const maxOffset = 12 - SHORT_DECK_MIN_RANK
  return Math.floor(classIndex / 13) <= maxOffset && classIndex % 13 <= maxOffset
}

const CLASS_INDEX: ReadonlyMap<string, number> = new Map(BUILT.names.map((n, i) => [n, i]))

export function handClassIndex(name: string): number {
  return CLASS_INDEX.get(name) ?? -1
}

/**
 * 两张牌 → classIndex。
 *
 * 不做合法性校验（热路径；非法 CardId 会算出越界索引而不是抛异常），
 * 调用方负责保证是 0..51 的两张不同牌。
 */
export function classIndexOfCards(a: CardId, b: CardId): number {
  const ra = rankOf(a)
  const rb = rankOf(b)
  const hi = ra >= rb ? ra : rb
  const lo = ra >= rb ? rb : ra
  // 行 = 12 - hi，列 = 12 - lo（对子与同花）；非同花走下三角，行列互换。
  if (ra === rb) return (12 - hi) * 13 + (12 - lo)
  const suited = suitOf(a) === suitOf(b)
  return suited ? (12 - hi) * 13 + (12 - lo) : (12 - lo) * 13 + (12 - hi)
}

export function classNameOfCards(a: CardId, b: CardId): string {
  return HAND_CLASSES[classIndexOfCards(a, b)]!
}

/**
 * classIndex → 一个规范代表（两张具体的牌）。
 *
 * 离线生成脚本用它枚举 169 个代表手牌。选哪两门花色不影响结果（花色对称），
 * 但固定下来能让生成产物逐位可复现。
 */
export function representativeOfClass(index: number): [CardId, CardId] {
  const row = Math.floor(index / 13)
  const col = index % 13
  const a = 12 - row
  const b = 12 - col
  if (a === b) return [makeCard(a, 0), makeCard(a, 1)] // 对子：梅花 + 方块
  if (a > b) return [makeCard(a, 0), makeCard(b, 0)] // 上三角：同花
  return [makeCard(b, 0), makeCard(a, 1)] // 下三角：非同花
}
